'use client';

/**
 * useChallenge — the single read path for pillar-challenge data.
 *
 * WHAT THIS REPLACES
 * ──────────────────
 * Five components each did their own `getDoc(users/{uid})` to resolve
 * the feature flag, then their own `getActiveChallenge`, and two of
 * them polled every 30 seconds. With the dashboard open that was on
 * the order of 120 Firestore reads a minute for data that changes a
 * handful of times a day.
 *
 * MIGRATION STATUS (accurate as of this file):
 *   Migrated onto this hook — ChallengeClient, PillarMissionCard,
 *   ChallengeMarginChip, app/more/page.tsx, and
 *   `components/challenge/RestartGate.tsx` (it consumes the hook including
 *   `pendingDecision`, and no longer does its own getDoc or
 *   getActiveChallenge). The "one user-doc read per uid" claim below now
 *   holds for every challenge consumer with no exceptions.
 *
 * This hook collapses all of it:
 *   - ONE user-doc read per uid, shared by every consumer via a
 *     module-level cache (the flag, the role, AND `challengesAppetite`
 *     all come off that same snapshot — the picker no longer needs a
 *     fifth getDoc for the appetite).
 *   - ONE `getActiveChallenge` + `evaluateToday` + `computeWeeklyProgress`
 *     pass, shared the same way. N mounted consumers = 1 fetch.
 *   - NO polling. Freshness comes from three signals instead:
 *       1. the `opmode-challenge-changed` event every engine mutation
 *          fires (see the AUDIT note below — some do not),
 *       2. `visibilitychange` when the tab/app comes back to the front,
 *       3. a 5-minute staleness check.
 *
 * WRITE DISCIPLINE
 * ────────────────
 * `evaluateToday` is a WRITE. It is called for `status === 'active'`
 * and nothing else — a paused, failed or completed arc is read-only
 * here. (The old 30s poll called it unconditionally for any truthy
 * challenge, which minted a fresh day doc for dead arcs twice a minute.)
 *
 * `catchUpRollover` is called here and NOWHERE else. That is a hard
 * requirement of the design, not a preference: it walks and settles
 * every unsettled day between the watermark and yesterday, and a
 * second call site racing it would double-count. It runs after
 * `getActiveChallenge` (it needs the arc) and before `evaluateToday`
 * (today's evaluation must start from post-catch-up counters).
 *
 * ─────────────────────────────────────────────────────────────────────
 * AUDIT — engine mutations that do NOT emit `opmode-challenge-changed`
 *
 * Dropping the poll makes the event the primary freshness signal, so
 * a mutation that stays silent leaves every other consumer stale until
 * the next visibility change or the 5-minute check. As of this file
 * these emit correctly:
 *   createChallenge · setCommitmentConfig · rolloverSingleDay ·
 *   rolloverDay · catchUpRollover · restartChallenge · endChallenge ·
 *   deleteChallenge · deleteAllChallenges · useGraceDay
 *
 * These do NOT emit (reported, not fixed — the engine is not this
 * file's to edit):
 *   evaluateToday · setCommitmentDone · swapCommitment ·
 *   updateChallenge · saveDailyStatus ·
 *   challengeEvidence.saveEvidenceToStatus
 *
 * `setCommitmentDone`, `swapCommitment` and `saveEvidenceToStatus` are
 * the ones that matter — they are user-visible mutations from one
 * surface that other mounted surfaces need to see. Until the engine
 * emits, callers must invoke `refresh()` from this hook after those
 * writes (that is what the returned `refresh` is for). `evaluateToday`
 * NOT emitting is correct and deliberate: this hook calls it, and an
 * emit there would loop.
 * ─────────────────────────────────────────────────────────────────────
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/lib/AuthContext';
import type { Challenge, DailyStatus } from '@/lib/pillarChallenges';
import {
  getActiveChallenge,
  catchUpRollover,
  evaluateToday,
  computeWeeklyProgress,
  type WeeklyProgressEntry,
  type RolloverResult,
} from '@/lib/pillarChallengesEngine';

// ============================================================================
// Types
// ============================================================================

/** `users/{uid}.challengesAppetite`, set in /onboarding-v2. */
export type ChallengeAppetite = 'suggest-one' | 'maybe-later' | 'not-interested';

/**
 * The slice of the user doc this feature cares about. Kept narrow on
 * purpose — consumers should not start reaching into arbitrary profile
 * fields through the challenge cache.
 */
export interface ChallengeUserDoc {
  /** Resolved once: `featureFlags.pillarChallenges === true || role === 'admin'`. */
  enabled: boolean;
  role: string | null;
  appetite: ChallengeAppetite | null;
}

/** Everything one fetch produces. Shared by reference across consumers. */
export interface ChallengeSnapshot {
  enabled: boolean;
  userDoc: ChallengeUserDoc | null;
  challenge: Challenge | null;
  status: DailyStatus | null;
  weeklyProgress: WeeklyProgressEntry[];
  /**
   * A failed day the catch-up walk stopped on and the user still owes a
   * decision for (grace / restart).
   *
   * Consumed by `components/challenge/RestartGate.tsx`, which decides
   * whether to raise the grace / restart gate off this value.
   */
  pendingDecision: RolloverResult | null;
  error: string | null;
  /** epoch ms — drives the staleness check. */
  fetchedAt: number;
}

export interface UseChallengeResult {
  /** Feature flag. False while the flag is still resolving. */
  enabled: boolean;
  challenge: Challenge | null;
  status: DailyStatus | null;
  weeklyProgress: WeeklyProgressEntry[];
  loading: boolean;
  error: string | null;
  /** Force a refetch, bypassing the cache. Call after any engine write. */
  refresh: () => Promise<void>;

  // ── Extras (same snapshot, no extra reads) ──────────────────────────
  /** True while Firebase auth is still resolving — distinct from signed-out. */
  authLoading: boolean;
  /** Auth has settled and there is a user. */
  signedIn: boolean;
  userDoc: ChallengeUserDoc | null;
  /** `users/{uid}.challengesAppetite` — the picker reads it from here. */
  appetite: ChallengeAppetite | null;
  pendingDecision: RolloverResult | null;
  /**
   * Re-arm local challenge notifications for the current challenge +
   * today's status. This is the mutation path: call it after a write
   * that changes the open-item count or the arc's status.
   */
  rescheduleNotifications: () => void;
}

// ============================================================================
// Module-level cache
// ============================================================================

/** Refetch when a snapshot is older than this and something pokes us. */
const STALE_MS = 5 * 60 * 1000;
/** How often the shared timer checks the staleness of live entries. */
const TICK_MS = 60 * 1000;

interface CacheEntry {
  snapshot: ChallengeSnapshot | null;
  inflight: Promise<ChallengeSnapshot> | null;
  /**
   * A forced fetch that is waiting for `inflight` to settle before it
   * starts. Coalesces every `force` that arrives during one fetch window
   * into ONE follow-up pass — see `load`.
   */
  queued: Promise<ChallengeSnapshot> | null;
  /**
   * An `opmode-challenge-changed` event arrived while `inflight` was
   * running. `refreshLive` cannot fetch immediately (that is the
   * feedback-loop guard), so it records the event here and one refetch
   * runs after the in-flight pass settles.
   */
  dirty: boolean;
  subscribers: Set<() => void>;
}

const cache = new Map<string, CacheEntry>();

function entryFor(uid: string): CacheEntry {
  let e = cache.get(uid);
  if (!e) {
    e = { snapshot: null, inflight: null, queued: null, dirty: false, subscribers: new Set() };
    cache.set(uid, e);
  }
  return e;
}

function publish(entry: CacheEntry, snapshot: ChallengeSnapshot): void {
  entry.snapshot = snapshot;
  // Copy before iterating: a subscriber's setState can unmount a sibling
  // which unsubscribes mid-iteration. (Array.from + forEach rather than
  // for-of over the Set — the project's tsconfig sets no `target`, so it
  // compiles at ES5 and iterating a Set/Map directly is a type error.)
  Array.from(entry.subscribers).forEach(notify => {
    try { notify(); } catch { /* a dead subscriber must not stop the rest */ }
  });
}

const EMPTY_WEEKLY: WeeklyProgressEntry[] = [];

// ============================================================================
// The fetch
// ============================================================================

async function readUserDoc(uid: string): Promise<ChallengeUserDoc> {
  const { doc, getDoc } = await import('firebase/firestore');
  const { db } = await import('@/lib/firebase');
  const snap = await getDoc(doc(db, 'users', uid));
  const data = snap.exists() ? (snap.data() as Record<string, any>) : null;
  const role = typeof data?.role === 'string' ? (data.role as string) : null;
  const rawAppetite = data?.challengesAppetite;
  const appetite: ChallengeAppetite | null =
    rawAppetite === 'suggest-one' || rawAppetite === 'maybe-later' || rawAppetite === 'not-interested'
      ? rawAppetite
      : null;
  return {
    // The ONLY copy of this expression. It was duplicated verbatim in
    // ChallengeClient, PillarMissionCard, ChallengeMarginChip,
    // RestartGate and app/more/page.tsx; all five now read it from here.
    enabled: data?.featureFlags?.pillarChallenges === true || role === 'admin',
    role,
    appetite,
  };
}

/**
 * One full pass: flag → active arc → catch-up rollover → today →
 * weekly progress. Never throws; failures land in `snapshot.error`.
 */
async function fetchSnapshot(uid: string): Promise<ChallengeSnapshot> {
  const base = (patch: Partial<ChallengeSnapshot>): ChallengeSnapshot => ({
    enabled: false,
    userDoc: null,
    challenge: null,
    status: null,
    weeklyProgress: EMPTY_WEEKLY,
    pendingDecision: null,
    error: null,
    fetchedAt: Date.now(),
    ...patch,
  });

  let userDoc: ChallengeUserDoc;
  try {
    userDoc = await readUserDoc(uid);
  } catch (err) {
    console.warn('[useChallenge] user-doc read failed:', err);
    // A read failure is an ERROR, not "no access" and not "no challenge".
    // Consumers must be able to tell the difference — routing a failed
    // read to the picker is how a user ends up with a duplicate arc.
    return base({ error: 'load-failed' });
  }

  if (!userDoc.enabled) return base({ enabled: false, userDoc });

  let challenge: Challenge | null;
  try {
    challenge = await getActiveChallenge(uid);
  } catch (err) {
    console.warn('[useChallenge] getActiveChallenge failed:', err);
    return base({ enabled: true, userDoc, error: 'load-failed' });
  }

  if (!challenge) return base({ enabled: true, userDoc });

  // ── Catch-up rollover. THE single call site. ──────────────────────
  // Settles every unsettled day between the watermark and yesterday and
  // threads the updated counters forward. It decides internally what to
  // do per status (completed: untouched · failed: parked · paused:
  // forgiven · active: walked), so it is safe to call for any arc and
  // must NOT be pre-filtered here.
  //
  // Best-effort: a catch-up failure must not blank the screen. We keep
  // the pre-catch-up challenge and carry on — the next pass retries.
  let pendingDecision: RolloverResult | null = null;
  try {
    const caught = await catchUpRollover(uid, challenge);
    challenge = caught.challenge;
    pendingDecision = caught.pendingDecision;
  } catch (err) {
    console.warn('[useChallenge] catchUpRollover failed:', err);
  }

  // ── Today. WRITE PATH — active arcs only. ─────────────────────────
  // A paused / failed / completed arc is read-only. evaluateToday
  // persists a day doc, and doing that for a dead arc is how the old
  // poll kept resurrecting day records for challenges nobody was
  // running.
  if (challenge.status !== 'active') {
    return base({ enabled: true, userDoc, challenge, pendingDecision });
  }

  let status: DailyStatus | null = null;
  try {
    status = await evaluateToday(uid, challenge);
  } catch (err) {
    console.warn('[useChallenge] evaluateToday failed:', err);
    return base({ enabled: true, userDoc, challenge, pendingDecision, error: 'load-failed' });
  }

  // Weekly targets are decorative — empty unless a commitment carries a
  // weeklyFrequency, and never worth failing the whole load over.
  let weeklyProgress: WeeklyProgressEntry[] = EMPTY_WEEKLY;
  try {
    weeklyProgress = await computeWeeklyProgress(uid, challenge);
  } catch { /* non-critical */ }

  const snapshot = base({ enabled: true, userDoc, challenge, status, weeklyProgress, pendingDecision });

  // The reschedule that used to live inside evaluateToday. See the
  // note on rescheduleNotifications below.
  scheduleNotificationsOnce(challenge, status);

  return snapshot;
}

/**
 * Deduped load. Concurrent callers share one in-flight promise, which is
 * the whole point of the cache: five components mounting in the same
 * tick produce one fetch, not five.
 *
 * `force` means "I just wrote something; give me a snapshot that can see
 * it". Joining the in-flight promise CANNOT satisfy that: the in-flight
 * fetch may have done its reads before the write landed. Concretely —
 * tap A → write A → refresh starts fetch F → tap B → write B commits →
 * a forced refresh that returned F resolves with a snapshot missing B,
 * and consumers that clear their optimistic override on a new snapshot
 * make item B visibly un-check itself. So a forced caller CHAINS a fresh
 * fetch off the in-flight one instead of joining it. Every force landing
 * in the same window shares that single follow-up (`entry.queued`), so
 * N rapid taps cost at most one extra pass, not N.
 */
function load(uid: string, opts: { force?: boolean } = {}): Promise<ChallengeSnapshot> {
  const entry = entryFor(uid);
  if (entry.inflight) {
    if (!opts.force) return entry.inflight;
    if (entry.queued) return entry.queued;
    const queued = entry.inflight
      .catch(() => undefined)
      .then(() => {
        // Clear FIRST: `load` below synchronously installs the new
        // `inflight`, so any force arriving after this point chains off
        // that one rather than joining a follow-up already consumed.
        entry.queued = null;
        // This follow-up IS the post-settle refetch a pending change
        // event was waiting for, so it discharges the dirty flag too —
        // otherwise the two paths would queue two identical passes.
        entry.dirty = false;
        return load(uid, { force: true });
      });
    entry.queued = queued;
    return queued;
  }
  if (!opts.force && entry.snapshot && Date.now() - entry.snapshot.fetchedAt < STALE_MS) {
    return Promise.resolve(entry.snapshot);
  }
  const p = fetchSnapshot(uid)
    .then(snapshot => {
      entry.inflight = null;
      publish(entry, snapshot);
      return snapshot;
    })
    .catch(err => {
      // fetchSnapshot swallows its own failures, so this is a genuine
      // programming error rather than a network blip. Still must not
      // leave `inflight` wedged — that would freeze every consumer.
      entry.inflight = null;
      console.warn('[useChallenge] load failed:', err);
      const snapshot: ChallengeSnapshot = {
        enabled: entry.snapshot?.enabled ?? false,
        userDoc: entry.snapshot?.userDoc ?? null,
        challenge: entry.snapshot?.challenge ?? null,
        status: entry.snapshot?.status ?? null,
        weeklyProgress: entry.snapshot?.weeklyProgress ?? EMPTY_WEEKLY,
        pendingDecision: entry.snapshot?.pendingDecision ?? null,
        error: 'load-failed',
        fetchedAt: Date.now(),
      };
      publish(entry, snapshot);
      return snapshot;
    });
  entry.inflight = p;
  return p;
}

/** Refetch every uid that currently has a mounted consumer. */
function refreshLive(opts: { force?: boolean; staleOnly?: boolean } = {}): void {
  const now = Date.now();
  cache.forEach((entry, uid) => {
    if (entry.subscribers.size === 0) return;
    // An in-flight fetch must not be joined by a second one. That guard
    // is what stops the feedback loop: catchUpRollover emits
    // `opmode-challenge-changed` from inside our own fetch, and fetching
    // on it synchronously would recurse forever.
    //
    // But it must not DROP the signal either — with the 30s poll gone,
    // a discarded event leaves every consumer stale until the next
    // visibility change or the 5-minute staleness check. So record it
    // and run exactly ONE refetch after the current pass settles.
    //
    // This terminates: catchUpRollover only emits when it actually
    // settled days (`if (days.length > 0)`), so the follow-up pass —
    // which finds nothing left to settle — emits nothing and the chain
    // ends. The flag is cleared before the refetch, so an event arriving
    // during the follow-up arms one more round rather than being lost.
    if (entry.inflight) {
      // The staleness tick wants freshness, nothing more, and an
      // in-flight pass delivers exactly that — no follow-up needed.
      if (opts.staleOnly) return;
      // A forced refetch is already chained off this fetch; it will see
      // the change. Nothing more to arm.
      if (entry.queued) return;
      if (entry.dirty) return;
      entry.dirty = true;
      void entry.inflight
        .catch(() => undefined)
        .then(() => {
          if (!entry.dirty) return;
          entry.dirty = false;
          if (entry.subscribers.size === 0) return;
          // Any fetch running at this instant necessarily STARTED after
          // the pass we were waiting on settled — therefore after the
          // change event — so its reads already see the change and a
          // second pass would be pure waste.
          if (entry.inflight) return;
          void load(uid, { force: true });
        });
      return;
    }
    if (opts.staleOnly && entry.snapshot && now - entry.snapshot.fetchedAt < STALE_MS) return;
    void load(uid, { force: opts.force ?? true });
  });
}

// ============================================================================
// Shared freshness signals — registered once for the whole module
// ============================================================================

let wired = false;
let tickTimer: ReturnType<typeof setInterval> | null = null;

function wireGlobalListeners(): void {
  if (wired || typeof window === 'undefined') return;
  wired = true;

  // 1. Every engine mutation that emits. Immediate invalidation.
  window.addEventListener('opmode-challenge-changed', () => refreshLive({ force: true }));

  // 2. Coming back to the front. The app may have been backgrounded
  //    across midnight, in which case catchUpRollover has real work.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') refreshLive({ force: true });
  });

  // 3. Staleness. This ticks on a timer but does NOT fetch on a timer —
  //    it only fetches when a snapshot is older than STALE_MS, so a
  //    foregrounded idle app costs one read pass per 5 minutes, not one
  //    per 30 seconds.
  tickTimer = setInterval(() => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    refreshLive({ force: true, staleOnly: true });
  }, TICK_MS);
  // Never unref'd: the module lives as long as the tab, and the tick is
  // a no-op whenever nothing is subscribed.
  void tickTimer;
}

// ============================================================================
// Notification mutation path
// ============================================================================

/**
 * Re-arm local challenge notifications.
 *
 * This used to fire from inside `evaluateToday`, which meant every read
 * of today's status churned the notification queue. It belongs on the
 * mutation path, which is here.
 *
 * NOTE (honest status): `evaluateToday` and `setCommitmentDone` in
 * `lib/pillarChallengesEngine.ts` STILL call
 * `scheduleChallengeNotifications` themselves — removing those two calls
 * is an engine edit and this file's owner does not own the engine. The
 * signature guard below means the hook's call collapses to a no-op once
 * the engine has already scheduled the same state, so the overlap costs
 * at most one extra cancel+schedule per signature change, and the
 * behaviour is already correct for the moment those engine calls are
 * deleted. On web it is a no-op regardless — the scheduler bails on
 * non-native platforms.
 */
let lastNotifSignature: string | null = null;

function notifSignature(challenge: Challenge, status: DailyStatus | null): string {
  const open = status ? Object.keys(status.completed ?? {}).sort().join(',') : '';
  return [
    challenge.id,
    challenge.status,
    status?.date ?? '',
    status?.dayNumber ?? '',
    open,
    JSON.stringify(challenge.notifications ?? null),
    // MUST be here. The user editing only a reminder TIME changes
    // nothing else in this signature, so without this line the guard
    // below short-circuits and the new times are never armed. The
    // downstream guard in pillarChallengeNotifications does include
    // them, but it never runs — this guard decides whether we call at
    // all. Any field that changes what gets scheduled belongs here.
    JSON.stringify(challenge.notificationTimes ?? null),
  ].join('|');
}

function scheduleNotificationsOnce(challenge: Challenge, status: DailyStatus | null): void {
  const sig = notifSignature(challenge, status);
  if (sig === lastNotifSignature) return;
  lastNotifSignature = sig;
  void (async () => {
    try {
      const { scheduleChallengeNotifications } = await import('@/lib/pillarChallengeNotifications');
      await scheduleChallengeNotifications(challenge, status);
    } catch (err) {
      console.warn('[useChallenge] notification reschedule failed:', err);
    }
  })();
}

// ============================================================================
// Hook
// ============================================================================

const EMPTY_RESULT = {
  enabled: false,
  challenge: null,
  status: null,
  weeklyProgress: EMPTY_WEEKLY,
  pendingDecision: null,
  userDoc: null,
  appetite: null,
  error: null,
} as const;

export function useChallenge(): UseChallengeResult {
  const { user, loading: authLoading } = useAuth();
  const uid = user?.uid ?? null;

  const [snapshot, setSnapshot] = useState<ChallengeSnapshot | null>(() =>
    uid ? entryFor(uid).snapshot : null,
  );
  const uidRef = useRef<string | null>(uid);
  uidRef.current = uid;

  useEffect(() => {
    if (!uid) {
      setSnapshot(null);
      return;
    }
    wireGlobalListeners();
    const entry = entryFor(uid);
    let cancelled = false;

    const sync = () => {
      if (cancelled) return;
      // Read through the cache rather than closing over a value: the
      // entry is the source of truth and may have been republished by
      // another consumer's fetch.
      setSnapshot(entry.snapshot);
    };
    entry.subscribers.add(sync);

    // Adopt whatever is already cached so a late-mounting consumer
    // paints immediately instead of flashing its own skeleton.
    if (entry.snapshot) sync();
    void load(uid);

    return () => {
      cancelled = true;
      entry.subscribers.delete(sync);
    };
  }, [uid]);

  const refresh = useCallback(async () => {
    const current = uidRef.current;
    if (!current) return;
    await load(current, { force: true });
  }, []);

  const rescheduleNotifications = useCallback(() => {
    const current = uidRef.current;
    if (!current) return;
    const snap = entryFor(current).snapshot;
    if (!snap?.challenge) return;
    // Force it past the signature guard — an explicit call from a
    // mutation path is asserting that something changed.
    lastNotifSignature = null;
    scheduleNotificationsOnce(snap.challenge, snap.status);
  }, []);

  const data = snapshot ?? EMPTY_RESULT;
  // Loading is true until auth settles, and then until this uid's first
  // snapshot lands. A signed-out visitor is NOT loading — that
  // distinction is what lets the router show a sign-in prompt instead of
  // an infinite spinner.
  const loading = authLoading || (!!uid && snapshot === null);

  return {
    enabled: data.enabled,
    challenge: data.challenge,
    status: data.status,
    weeklyProgress: data.weeklyProgress,
    loading,
    error: data.error,
    refresh,
    authLoading,
    signedIn: !authLoading && !!uid,
    userDoc: data.userDoc,
    appetite: data.userDoc?.appetite ?? null,
    pendingDecision: data.pendingDecision,
    rescheduleNotifications,
  };
}

export default useChallenge;
